import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import alertService from '../../services/AlertService.js';
import authService from '../../services/authService.js';
import Alert from './Alert.js';
import Button from './Button.js';
import { useTranslation } from 'react-i18next';
import { shouldHideMlSheetDispatch } from '../../utils/networkMode.js';

const alertPath = (alert, user) => {
  const kind = String(alert?.reference_type || '').toLowerCase();
  if (kind === 'daily_branch_digest') return '/reports/daily-branch';
  if (kind === 'agent_outstanding_digest') return '/reports/goods-balance';
  if (kind === 'delivery_efficiency_digest') return '/reports/delivery-event';
  if (kind === 'company_paid') return '/company-settlements?view=company_paid';
  if (kind.includes('company_settlement')) {
    const batchId = String(alert?.reference_id || '').trim();
    return batchId
      ? `/company-settlement-approvals?batch_id=${encodeURIComponent(batchId)}`
      : '/company-settlement-approvals';
  }
  if (kind.includes('paid_amount') || kind.includes('ml_agent_settlement')) {
    return '/company-settlement-approvals';
  }
  if (kind === 'itcl_request' || kind.includes('itcl')) {
    const requestId = String(alert?.reference_id || '').trim();
    return requestId
      ? `/ogcl-transfer-requests/view/${encodeURIComponent(requestId)}`
      : '/ogcl-transfer-requests';
  }
  if (kind === 'expense') return '/expenses';
  if (kind === 'ml_crossing_complete') {
    const mlId = String(alert?.reference_id || '').trim();
    return mlId
      ? `/ml-sheets/view/${encodeURIComponent(mlId)}`
      : '/ml-sheets';
  }
  if (kind.includes('ml_sheet')) {
    return shouldHideMlSheetDispatch(user) ? '/ogcl-transfer-requests' : '/ml-sheets';
  }
  return null;
};

const CATEGORY_LABELS = {
  daily_branch_digest: 'Daily branch',
  agent_outstanding_digest: 'Outstanding',
  delivery_efficiency_digest: 'Delivery',
  payable_due: 'Payable',
  expense: 'Expense',
  company_paid: 'Company paid',
  company_settlement: 'Settlement',
  ml_sheet: 'ML sheet',
  ml_crossing_complete: 'Crossing',
  itcl_request: 'Transfer',
};

const categoryKey = (alert) => String(alert?.reference_type || 'other').toLowerCase();

const categoryLabel = (key) => {
  if (CATEGORY_LABELS[key]) return CATEGORY_LABELS[key];
  const text = String(key || 'other').replace(/_/g, ' ').replace(/\bdigest\b/gi, '').trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : 'Other';
};

const AlertModal = ({ isOpen, onClose, userId, onUnreadChange }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('unread');
  const [category, setCategory] = useState('all');
  const [unreadAlerts, setUnreadAlerts] = useState([]);
  const [readAlerts, setReadAlerts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && userId) {
      fetchAlerts();
    }
  }, [isOpen, userId, activeTab]);

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const unreadResponse = await alertService.getUnread(userId);
      const unread = unreadResponse.data || [];
      setUnreadAlerts(unread);
      onUnreadChange?.(unread.length);

      if (activeTab === 'read') {
        const readResponse = await alertService.getRead(userId);
        setReadAlerts(readResponse.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch alerts:', err);
      Alert.error('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAllAsRead = async () => {
    const confirmed = await Alert.confirm(
      'Mark all unread alerts as read?',
      'Confirm Action'
    );
    if (confirmed) {
      try {
        await alertService.markAllAsRead(userId);
        Alert.success('All alerts marked as read');
        fetchAlerts();
      } catch (err) {
        Alert.error('Failed to mark alerts as read');
      }
    }
  };

  const handleClearAllUnread = async () => {
    const confirmed = await Alert.confirm(
      'Delete all unread alerts permanently?',
      'Confirm Delete'
    );
    if (confirmed) {
      try {
        await alertService.deleteAllUnread(userId);
        Alert.success('All unread alerts deleted');
        fetchAlerts();
      } catch (err) {
        Alert.error('Failed to delete unread alerts');
      }
    }
  };

  const handleClearAllRead = async () => {
    const confirmed = await Alert.confirm(
      'Delete all read alerts permanently?',
      'Confirm Delete'
    );
    if (confirmed) {
      try {
        await alertService.deleteAllRead(userId);
        Alert.success('All read alerts deleted');
        fetchAlerts();
      } catch (err) {
        Alert.error('Failed to delete read alerts');
      }
    }
  };

  const handleDeleteAlert = async (alertId) => {
    try {
      await alertService.deleteAlert(alertId);
      Alert.success('Alert deleted');
      fetchAlerts();
    } catch (err) {
      Alert.error('Failed to delete alert');
    }
  };

  const handleMarkAsRead = async (alertId) => {
    try {
      await alertService.markAsRead(alertId);
      fetchAlerts();
    } catch (err) {
      Alert.error('Failed to mark alert as read');
    }
  };

  const openAlert = async (alert) => {
    const path = alertPath(alert, authService.getCurrentUser());
    if (alert && !alert.is_read) {
      try {
        await alertService.markAsRead(alert.id);
      } catch {
        /* still navigate */
      }
    }
    if (path) {
      onClose?.();
      navigate(path);
    }
  };

  const getAlertTypeColor = (type) => {
    const colors = {
      info: 'bg-blue-50 border-blue-200 text-blue-800',
      warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
      error: 'bg-red-50 border-red-200 text-red-800',
      success: 'bg-green-50 border-green-200 text-green-800'
    };
    return colors[type] || colors.info;
  };

  const getAlertTypeIcon = (type) => {
    const icons = {
      info: (
        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
      ),
      warning: (
        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
      ),
      error: (
        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
      ),
      success: (
        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
      )
    };
    return icons[type] || icons.info;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-IN', { 
      day: '2-digit', 
      month: 'short',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  if (!isOpen) return null;

  const tabAlerts = activeTab === 'unread' ? unreadAlerts : readAlerts;
  const categoryCounts = tabAlerts.reduce((counts, alert) => {
    const key = categoryKey(alert);
    counts[key] = (counts[key] || 0) + 1;
    return counts;
  }, {});
  const categories = Object.entries(categoryCounts).sort((a, b) => b[1] - a[1]);
  const activeCategory = category !== 'all' && categoryCounts[category] ? category : 'all';
  const currentAlerts = activeCategory === 'all'
    ? tabAlerts
    : tabAlerts.filter((alert) => categoryKey(alert) === activeCategory);

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      <div 
        className="bg-sky-50 w-full sm:max-w-2xl sm:rounded-lg shadow-xl h-[85vh] sm:h-auto sm:max-h-[85vh] flex flex-col overflow-hidden rounded-t-3xl sm:rounded-b-lg border border-sky-100"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Mobile Pull Indicator */}
        <div className="flex sm:hidden shrink-0 justify-center pt-3 pb-2">
          <div className="w-12 h-1.5 bg-sky-300 rounded-full"></div>
        </div>

        {/* Header */}
        <div className="shrink-0 grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 px-4 sm:px-5 py-3 border-b border-sky-100 bg-sky-50 rounded-t-3xl sm:rounded-t-lg">
          <h2 className="text-base sm:text-lg font-semibold text-sky-950 whitespace-nowrap">Notifications</h2>
          <div className="flex flex-wrap items-center justify-center gap-1.5 min-w-0">
            {activeTab === 'unread' ? (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleMarkAllAsRead}
                  disabled={unreadAlerts.length === 0}
                  className="text-blue-600 bg-blue-50 hover:bg-blue-100 rounded touch-manipulation !px-2.5 !py-1.5 !text-xs"
                >
                  Mark all as read
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearAllUnread}
                  disabled={unreadAlerts.length === 0}
                  className="text-red-600 bg-red-50 hover:bg-red-100 rounded touch-manipulation !px-2.5 !py-1.5 !text-xs"
                >
                  Clear all unread
                </Button>
              </>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearAllRead}
                disabled={readAlerts.length === 0}
                className="text-red-600 bg-red-50 hover:bg-red-100 rounded touch-manipulation !px-2.5 !py-1.5 !text-xs"
              >
                Clear all read
              </Button>
            )}
          </div>
          <Button
            variant="ghost"
            onClick={onClose}
            className="shrink-0 p-1.5 rounded-full touch-manipulation hover:bg-sky-100"
          >
            <svg className="w-5 h-5 text-sky-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </Button>
        </div>

        {/* Tabs */}
        <div className="shrink-0 flex border-b border-sky-100 bg-sky-50">
          <Button
            variant="ghost"
            onClick={() => setActiveTab('unread')}
            className={`flex-1 px-3 sm:px-4 py-2.5 text-xs sm:text-sm rounded-none touch-manipulation ${
              activeTab === 'unread'
                ? 'text-sky-700 border-b-2 border-sky-600 bg-sky-100/80'
                : 'text-gray-600 hover:text-sky-800 hover:bg-sky-50'
            }`}
          >
            Unread
            {unreadAlerts.length > 0 && (
              <span className={`ml-1.5 px-1.5 py-0.5 text-[10px] sm:text-xs font-bold rounded-full ${
                activeTab === 'unread' 
                  ? 'bg-sky-600 text-white' 
                  : 'bg-sky-100 text-sky-800'
              }`}>
                {unreadAlerts.length}
              </span>
            )}
          </Button>
          <Button
            variant="ghost"
            onClick={() => setActiveTab('read')}
            className={`flex-1 px-3 sm:px-4 py-2.5 text-xs sm:text-sm rounded-none touch-manipulation ${
              activeTab === 'read'
                ? 'text-sky-700 border-b-2 border-sky-600 bg-sky-100/80'
                : 'text-gray-600 hover:text-sky-800 hover:bg-sky-50'
            }`}
          >
            Read
            {readAlerts.length > 0 && (
              <span className={`ml-1.5 px-1.5 py-0.5 text-[10px] sm:text-xs font-bold rounded-full ${
                activeTab === 'read' 
                  ? 'bg-sky-600 text-white' 
                  : 'bg-sky-100 text-sky-800'
              }`}>
                {readAlerts.length}
              </span>
            )}
          </Button>
        </div>

        {categories.length > 0 && (
          <div className="shrink-0 flex flex-wrap gap-2 px-3 sm:px-4 py-2.5 bg-sky-50 border-b border-sky-200">
            <button
              type="button"
              onClick={() => setCategory('all')}
              className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-xs font-semibold leading-none ${
                activeCategory === 'all' ? 'bg-sky-600 text-white' : 'bg-white text-sky-800 border border-sky-200'
              }`}
            >
              All
              <span className={`min-w-[1.125rem] px-1 py-0.5 rounded-full text-[10px] font-bold text-center ${
                activeCategory === 'all' ? 'bg-white/20 text-white' : 'bg-sky-100 text-sky-800'
              }`}>
                {tabAlerts.length}
              </span>
            </button>
            {categories.map(([key, count]) => (
              <button
                key={key}
                type="button"
                onClick={() => setCategory(key)}
                className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-xs font-semibold leading-none ${
                  activeCategory === key ? 'bg-sky-600 text-white' : 'bg-white text-sky-800 border border-sky-200'
                }`}
              >
                {categoryLabel(key)}
                <span className={`min-w-[1.125rem] px-1 py-0.5 rounded-full text-[10px] font-bold text-center ${
                  activeCategory === key ? 'bg-white/20 text-white' : 'bg-sky-100 text-sky-800'
                }`}>
                  {count}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Alert List */}
        <div className="flex-auto min-h-0 overflow-y-auto overscroll-contain bg-white">
          {loading ? (
            <div className="flex items-center justify-center py-16 sm:py-20">
              <div className="text-center">
                <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-sky-500 mx-auto mb-4"></div>
                <p className="text-sky-800 text-xs sm:text-sm">Loading notifications...</p>
              </div>
            </div>
          ) : currentAlerts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 sm:py-20 px-4">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-sky-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 sm:w-10 sm:h-10 text-sky-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
              </div>
              <p className="text-sky-950 text-sm sm:text-base font-semibold mb-1">No {activeTab} alerts</p>
              <p className="text-sky-700/70 text-xs sm:text-sm">You're all caught up!</p>
            </div>
          ) : (
            <div className="divide-y divide-sky-100 bg-white">
              {currentAlerts.map((alert) => (
                <div
                  key={alert.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => openAlert(alert)}
                  onKeyDown={(e) => { if (e.key === 'Enter') openAlert(alert); }}
                  className={`p-3 sm:p-4 hover:bg-sky-50 transition-colors active:bg-sky-100 cursor-pointer ${
                    activeTab === 'unread' ? 'bg-sky-50/70' : 'bg-white'
                  }`}
                >
                  <div className="flex gap-2.5 sm:gap-3">
                    {/* Icon */}
                    <div className={`flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center ${getAlertTypeColor(alert.alert_type)}`}>
                      {getAlertTypeIcon(alert.alert_type)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1.5 sm:mb-2">
                        <p className="text-xs sm:text-sm text-gray-900 break-words whitespace-pre-line leading-relaxed pr-2">
                          {alert.message}
                        </p>
                        <Button
                          variant="ghost"
                          onClick={(e) => { e.stopPropagation(); handleDeleteAlert(alert.id); }}
                          className="flex-shrink-0 p-1.5 sm:p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full touch-manipulation active:bg-red-100"
                          title="Delete alert"
                        >
                          <svg className="w-4 h-4 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </Button>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] sm:text-xs text-gray-500">
                        <span className="font-medium">{formatDate(alert.created_at)}</span>
                        {alert.reference_type && (
                          <span className="bg-sky-100 text-sky-800 px-2 py-0.5 rounded-full text-[10px] font-semibold leading-none">
                            {categoryLabel(categoryKey(alert))}
                          </span>
                        )}
                        {activeTab === 'unread' && (
                          <Button
                              variant="link"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleMarkAsRead(alert.id);
                              }}
                              className="hover:text-blue-700 touch-manipulation active:text-blue-800"
                            >
                              Mark as read
                            </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Mobile Bottom Safe Area */}
        <div className="h-safe-area-inset-bottom sm:hidden shrink-0 bg-sky-50"></div>
      </div>
    </div>
  );
};

export default AlertModal;